import React, { useEffect, useState } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import DateFilter from '../../components/DateFilter/DateFilter';
import Toast from '../../components/Toast/Toast';
import { getPerformance, downloadPerformanceReport } from '../../services/OrdersService';
import { processError } from '../../services/ServiceUtils';
import { getErrorMessage, getEmptyMessage } from '../../utils/MessageUtils';
import dateUtils, { MONTH_MODE } from '../../utils/DateUtils';
import { exportToCsv } from '../../utils/CsvUtils';
import EquityCurve from './EquityCurve';
import AutomationStats from './AutomationStats';
import RegimeStats from './RegimeStats';
import AiSignalStats from './AiSignalStats';
import AiSignalHistory from './AiSignalHistory';
import RegimeHistory from './RegimeHistory';
import FocusHistory from './FocusHistory';
import HeadlineMetrics from './HeadlineMetrics';
import ResultsBreakdown from './ResultsBreakdown';
import LossAnalysisPanel from './LossAnalysisPanel';
import OpenPositions from '../Dashboard/OpenPositions';
import AiAccuracyByProvider from './AiAccuracyByProvider';
import PostMortemModal from './PostMortemModal';
import BusinessHealthBanner from '../../components/BusinessHealthBanner/BusinessHealthBanner';
import { getCurrentEnvironment, subscribeToCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import { getCurrentMarket, subscribeToCurrentMarket, MARKET_FUTURES } from '../../utils/MarketStorage';

function Performance() {
    const [filter, setFilter] = useState({
        dateMode: MONTH_MODE,
        startDate: dateUtils.getInicialStartDate(MONTH_MODE),
        endDate: dateUtils.getInicialEndDate(),
        environment: getCurrentEnvironment(),
        market: getCurrentMarket()
    });
    const [data, setData] = useState(null);
    const [notification, setNotification] = useState(getEmptyMessage());
    const [postMortemOrderId, setPostMortemOrderId] = useState(null);
    const isFutures = filter.market === MARKET_FUTURES;

    useEffect(() => subscribeToCurrentEnvironment(env =>
        setFilter(prev => ({ ...prev, environment: env }))), []);
    useEffect(() => subscribeToCurrentMarket(m =>
        setFilter(prev => ({ ...prev, market: m }))), []);

    function onDateChange(event) {
        const value = event.target.value;
        if (value.modeChanged === true) {
            value.startDate = dateUtils.getInicialStartDate(value.dateMode);
            value.endDate = dateUtils.getInicialEndDate();
        }
        setFilter(prev => ({
            ...prev,
            dateMode: value.dateMode,
            startDate: value.startDate,
            endDate: value.endDate
        }));
    }

    function onExportClick() {
        if (!data) return;
        const headers = ['Data', 'Compras', 'Vendas', 'P&L'];
        const rows = (data.equityCurve || []).map(p => [
            p.date,
            p.buy,
            p.sell,
            p.profit
        ]);
        exportToCsv(`performance_${Date.now()}.csv`, headers, rows);
    }

    const [downloadingReport, setDownloadingReport] = useState(false);
    function onExportReportClick() {
        setDownloadingReport(true);
        downloadPerformanceReport(filter.startDate, filter.endDate, filter.environment, filter.market)
            .catch(err => setNotification(getErrorMessage(err.message || 'Falha ao gerar relatório')))
            .finally(() => setDownloadingReport(false));
    }

    useEffect(() => {
        if (!filter.startDate || !filter.endDate) return;

        getPerformance(filter.startDate, filter.endDate, filter.environment, filter.market)
            .then(result => setData(result))
            .catch(err => setNotification(getErrorMessage(processError(err))));
    }, [filter.startDate, filter.endDate, filter.environment, filter.market]);

    return (
        <React.Fragment>
            <Menu />
            <main className='content'>
                <div className='row py-4 align-items-center'>
                    <div className='col-6'>
                        <h2 className='h4 mb-0'>
                            Performance
                            <span className={`badge ms-2 ${isFutures ? 'bg-warning text-dark' : 'bg-secondary'}`} style={{ fontSize: '0.5em', verticalAlign: 'middle' }}>
                                {isFutures ? 'Futuros' : 'Spot'}
                            </span>
                        </h2>
                    </div>
                    <div className='col-6 d-flex justify-content-end gap-2 align-items-center'>
                        <button
                            className='btn btn-sm btn-outline-info'
                            onClick={onExportReportClick}
                            disabled={downloadingReport || !data}
                            title='Baixa relatório em Markdown — útil pra colar em chat de IA pra análise externa'>
                            {downloadingReport ? '...' : '📥 Relatório'}
                        </button>
                        <button
                            className='btn btn-sm btn-outline-secondary'
                            onClick={onExportClick}
                            disabled={!data || !(data.equityCurve && data.equityCurve.length)}
                            title='Exportar curva de equity como CSV'
                        >
                            CSV
                        </button>
                        <DateFilter
                            dateMode={filter.dateMode}
                            startDate={filter.startDate}
                            endDate={filter.endDate}
                            onClick={onDateChange}
                        />
                    </div>
                </div>
                <BusinessHealthBanner linkToPerformance={false} />
                <HeadlineMetrics data={data ? data.headline : null} />
                <ResultsBreakdown data={data ? data.results : null} />
                <LossAnalysisPanel startDate={filter.startDate} endDate={filter.endDate} environment={filter.environment} market={filter.market} />
                <OpenPositions tickerData={{}} />
                <EquityCurve data={data ? data.equityCurve : null} onPointClick={setPostMortemOrderId} />
                <AutomationStats data={data ? data.automations : null} />
                <RegimeStats data={data ? data.regimes : null} />
                <RegimeHistory startDate={filter.startDate} endDate={filter.endDate} />
                <FocusHistory startDate={filter.startDate} endDate={filter.endDate} />
                <AiSignalStats data={data ? data.aiSignals : null} />
                <AiAccuracyByProvider />
                <AiSignalHistory />

                <Footer />
            </main>
            <PostMortemModal orderId={postMortemOrderId} onClose={() => setPostMortemOrderId(null)} />
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default Performance;
